import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Button,
  Card,
  Empty,
  Alert,
  Space,
  Spin,
  Tag,
  Typography,
  Select,
  message,
} from "antd";
import { ArrowLeftOutlined, ReloadOutlined } from "@ant-design/icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import InstitutionAccountsTable from "../../../components/accounts/InstitutionAccountsTable";
import AccountTransactionModal from "../../../components/transaction/AccountTransactionModal";
import { getTypeColor } from "../../../utils/themeUtils";
import WalletCard from "../../../components/balances/WalletCard";
import { createInstitutionWalletColumns } from "../../../components/balances/institutionWalletColumns";
import { useInstitutionWallets } from "./hooks/useInstitutionWallets";
import { useInstitutionWalletDashboard } from "./hooks/useInstitutionWalletDashboard";
import InstitutionWalletWeeklyDashboard from "../../../components/dashboard/InstitutionWalletWeeklyDashboard";
import { deactivateAccount, activateAccount } from "../../../api/bankAccountApi";
import { hasAnyRole } from "../../../api/auth";

const { Title } = Typography;

export default function InstitutionWalletsPage() {
  const { institutionId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currencyFromUrl = searchParams.get("currency");
  const walletRefs = useRef({});
  const [selectedBank, setSelectedBank] = useState("ALL");

  const canOpenManualModal = hasAnyRole(["ADMIN", "MODERATOR"]);

  const {
    institution,
    groupedByCurrency,
    selectedCurrency,
    selectedCurrencyGroup,
    loading,
    error,
    transactionModalOpen,
    transactionMode,
    selectedAccount,
    transactionSubmitting,
    manualCurrencyMatchedSenderAccounts,
    internalInstitutionAccounts,
    setSelectedCurrency,
    loadData,
    closeTransactionModal,
    openManualModal,
    openTransferModal,
    handleSubmitTransaction,
    manualSenderInstitutionId,
    externalInstitutions,
    externalInstitutionsLoading,
    handleTransferScopeChange,
  } = useInstitutionWallets(institutionId, navigate, currencyFromUrl);

  const {
    dashboardData,
    dashboardLoading,
    dashboardError,
    loadDashboard,
  } = useInstitutionWalletDashboard(institutionId);

  const handleToggleActive = useCallback(
    async (record) => {
      try {
        if (record?.is_active) {
          await deactivateAccount(record.id);
          message.success("Данс амжилттай идэвхгүй боллоо");
        } else {
          await activateAccount(record.id);
          message.success("Данс амжилттай идэвхжлээ");
        }

        await Promise.all([loadData(), loadDashboard()]);
      } catch (err) {
        console.error(err);
        message.error("Дансны төлөв шинэчлэхэд алдаа гарлаа");
      }
    },
    [loadData, loadDashboard]
  );

  const handleOpenManualModal = useCallback(
    (record) => {
      if (!canOpenManualModal) {
        message.error("Танд гараар гүйлгээ нээх эрх алга");
        return;
      }

      openManualModal(record);
    },
    [canOpenManualModal, openManualModal]
  );

  const handleOpenTransferModal = useCallback(
    (record) => {
      openTransferModal(record);
    },
    [openTransferModal]
  );

  const accountColumns = createInstitutionWalletColumns({
    onManual: handleOpenManualModal,
    onTransfer: handleOpenTransferModal,
    onToggleActive: handleToggleActive,
    canOpenManualModal,
  });

  const handleSelectCurrency = (currency) => {
    setSelectedCurrency(currency);
    setSelectedBank("ALL");

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (currency) {
        next.set("currency", currency);
      } else {
        next.delete("currency");
      }
      return next;
    });
  };

  const filteredAccountsSource = useMemo(() => {
    if (
      Array.isArray(selectedCurrencyGroup?.accounts) &&
      selectedCurrencyGroup.accounts.length > 0
    ) {
      return selectedCurrencyGroup.accounts;
    }

    return groupedByCurrency.flatMap((group) =>
      Array.isArray(group.accounts) ? group.accounts : []
    );
  }, [selectedCurrencyGroup, groupedByCurrency]);

  const bankOptions = useMemo(() => {
    return [
      ...new Set(
        filteredAccountsSource
          .map((account) => account?.bank)
          .filter((bank) => typeof bank === "string" && bank.trim() !== "")
      ),
    ];
  }, [filteredAccountsSource]);

  const filteredAccounts = useMemo(() => {
    const accounts = selectedCurrencyGroup?.accounts || [];
    if (selectedBank === "ALL") return accounts;
    return accounts.filter((account) => account.bank === selectedBank);
  }, [selectedCurrencyGroup, selectedBank]);

  useEffect(() => {
    if (!selectedCurrency) return;

    const node = walletRefs.current[selectedCurrency];
    if (!node) return;

    node.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [selectedCurrency]);

  useEffect(() => {
    setSelectedBank("ALL");
  }, [selectedCurrency]);

  useEffect(() => {
    if (!groupedByCurrency?.length) return;
    if (selectedCurrency) return;

    const cny = groupedByCurrency.find((g) => g.currency === "CNY");

    if (cny) {
      setSelectedCurrency("CNY");
      return;
    }

    setSelectedCurrency(groupedByCurrency[0].currency);
  }, [groupedByCurrency, selectedCurrency, setSelectedCurrency]);

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!institution && !error) {
    return <Empty description="Байгууллага олдсонгүй" />;
  }

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-8">
      <div className="mx-auto">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/manage/institutions")}
          className="mb-4"
        >
          Буцах
        </Button>

        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <Title level={2} className="!mb-1">
              {institution?.name || "Байгууллага"}
            </Title>
            <Tag color={getTypeColor(institution?.type || "UNKNOWN")}>
              {institution?.type || "ТОДОРХОЙГҮЙ"}
            </Tag>
          </div>

          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={async () => {
                await Promise.all([loadData(), loadDashboard()]);
              }}
            >
              Шинэчлэх
            </Button>
          </Space>
        </div>

        {error ? (
          <Card className="mb-6">
            <Alert
              type="error"
              title="Ачааллахад алдаа гарлаа"
              description={error}
              showIcon
            />
          </Card>
        ) : null}

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_460px]">
          <div className="space-y-6">
            <Card
              title="Валютууд"
              className="rounded-2xl shadow-md"
              styles={{ body: { padding: 20 } }}
            >
              {groupedByCurrency.length === 0 ? (
                <Empty description="Энэ байгууллагад данс олдсонгүй" />
              ) : (
                <div className="-mx-1 overflow-x-auto overflow-y-hidden pb-3">
                  <div className="flex min-w-max gap-4 p-2">
                    {[...groupedByCurrency]
                      .sort((a, b) => {
                        if (a.currency === "CNY") return -1;
                        if (b.currency === "CNY") return 1;
                        return 0;
                      })
                      .map((group) => (
                        <div
                          key={group.currency}
                          ref={(node) => {
                            if (node) {
                              walletRefs.current[group.currency] = node;
                            }
                          }}
                          className="shrink-0"
                        >
                          <WalletCard
                            currency={group.currency}
                            walletBalance={group.walletBalance}
                            totalBalance={group.totalBalance}
                            accountCount={group.accounts.length}
                            active={selectedCurrency === group.currency}
                            onClick={() => handleSelectCurrency(group.currency)}
                          />
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </Card>

            <Card
              className="rounded-2xl shadow-md"
              styles={{ body: { padding: 20 } }}
            >
              <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                  <div className="text-base font-semibold text-slate-700">
                    Банкны шүүлтүүр
                  </div>
                </div>

                <div className="w-full md:w-[260px]">
                  <Select
                    value={selectedBank}
                    onChange={setSelectedBank}
                    className="w-full"
                    placeholder="Банк сонгох"
                    options={[
                      { value: "ALL", label: "Бүх банк" },
                      ...bankOptions.map((bank) => ({
                        value: bank,
                        label: bank,
                      })),
                    ]}
                  />
                </div>
              </div>
            </Card>

            <InstitutionAccountsTable
              title={
                selectedCurrencyGroup
                  ? `${selectedCurrencyGroup.currency} данснууд`
                  : "Данснууд"
              }
              subtitle={
                selectedBank === "ALL"
                  ? ""
                  : `Банкаар шүүсэн: ${selectedBank}`
              }
              loading={loading}
              accounts={filteredAccounts}
              columns={accountColumns}
            />
          </div>

          <div>
            <div className="xl:sticky xl:top-6">
              <InstitutionWalletWeeklyDashboard
                institutionName = {institution.name}
                institutionType = {institution.type}
                institutionId={institutionId}
                data={dashboardData}
                selectedCurrency={selectedCurrency}
                loading={dashboardLoading}
                error={dashboardError}
              />
            </div>
          </div>
        </div>

        <AccountTransactionModal
          title={`${institution?.name}`}
          open={transactionModalOpen}
          mode={transactionMode}
          selectedAccount={selectedAccount}
          manualSenderInstitutionId={manualSenderInstitutionId}
          manualSenderAccounts={manualCurrencyMatchedSenderAccounts}
          institutionAccounts={internalInstitutionAccounts}
          externalInstitutions={externalInstitutions}
          externalInstitutionsLoading={externalInstitutionsLoading}
          onTransferScopeChange={handleTransferScopeChange}
          onCancel={closeTransactionModal}
          onSubmit={handleSubmitTransaction}
          loading={transactionSubmitting}
          loadDashboard = {loadDashboard}
          loadData = {loadData}
        />
      </div>
    </div>
  );
}